import { Injectable, signal } from '@angular/core';

// ---------------------------------------------------------------------------
// Typed error so callers can cleanly fall back (e.g. to browser speechSynthesis)
// ---------------------------------------------------------------------------

/** Shape of the `predict` function exported by vits-web-local.js */
type PiperProgressCallback = (p: { url: string; total: number; loaded: number }) => void;
type PiperPredictFn = (opts: { text: string; voiceId: string }, onProgress?: PiperProgressCallback) => Promise<Blob>;

export class NarrationTtsError extends Error {
  readonly originalCause: unknown;
  constructor(message: string, originalCause?: unknown) {
    super(message);
    this.name = 'NarrationTtsError';
    this.originalCause = originalCause;
  }
}

export interface NarrationTtsResult {
  blob: Blob;
  objectUrl: string;
  durationSeconds: number;
}

/**
 * Shared in-browser TTS using Piper/VITS via @diffusionstudio/vits-web.
 *
 * ARCHITECTURE — why these unusual import patterns are required:
 *
 * 1. The library is self-hosted at /assets/piper/vits-web-local.js.
 *    It is a patched copy of the npm dist with CDN URLs replaced by local
 *    /assets/piper/ paths for onnxruntime-web wasm files and piper_phonemize
 *    wasm/data, so TTS works offline after the first Piper model download.
 *
 * 2. esbuild (Angular 21 builder) must NOT bundle the lib:
 *    - The lib's inner worker chunk (piper-DeOu3H9E.js) uses require("fs")/
 *      require("path"), which would fail if bundled.
 *    - The lib loads onnxruntime-web by URL at runtime; bundling it would
 *      re-introduce Node type leaks via @types/node.
 *    Solution: load vits-web-local.js at runtime via a VARIABLE-specifier
 *    dynamic import. esbuild only bundles string-literal specifiers; a
 *    computed string is left as a native browser import() call.
 *
 * 3. TypeScript must NOT pull in the library's type graph (which carries
 *    `/// <reference types="node" />` via onnxruntime-web types).
 *    Solution: the import resolves to `any` — the variable specifier means
 *    TS infers Promise<any> and skips module type resolution entirely.
 *
 * Piper ONNX voice models are fetched from HuggingFace on first use and
 * cached in OPFS by the library (no server-side storage needed).
 */
@Injectable({ providedIn: 'root' })
export class NarrationTtsService {
  /**
   * Default Piper voice: en_US-hfc_female-medium.
   * This is a high-quality American English female voice available in the
   * vits-web voice list. Callers may pass any key from the vits-web PATH_MAP.
   */
  readonly defaultVoiceId = 'en_US-hfc_female-medium';

  /**
   * URL of the self-hosted vits-web ESM. Assigned to a `const` so it is
   * a non-literal string at the call site — esbuild sees `import(url)` where
   * `url` is a variable and leaves the expression as a native dynamic import.
   */
  private readonly _libUrl = '/assets/piper/vits-web-local.js';

  /** True while the model is downloading/initialising (first use only). */
  readonly modelLoading = signal<boolean>(false);
  /** 0–100 download progress during the first model fetch (best-effort). */
  readonly modelProgress = signal<number>(0);
  /** Flips true once the model has finished loading at least once. */
  readonly modelReady = signal<boolean>(false);

  /** Cached reference to the `predict` function exported by vits-web. */
  private _predict: PiperPredictFn | null = null;
  private _loadPromise: Promise<PiperPredictFn | null> | null = null;

  // ---------------------------------------------------------------------------

  isSupported(): boolean {
    if (typeof window === 'undefined') return false;
    if (!('WebAssembly' in window)) return false;
    if (!('AudioContext' in window || 'webkitAudioContext' in window)) return false;
    return true;
  }

  /** Best-effort warm of the model so the first synthesize() isn't a cold load. */
  async preload(voiceId?: string): Promise<void> {
    if (!this.isSupported()) return;
    try {
      await this._getPredict();
    } catch (err) {
      console.warn('[NarrationTts] preload failed (non-fatal):', err);
    }
  }

  /**
   * Synthesise `text` to a WAV Blob via Piper/VITS. Returns the blob, an
   * object URL (caller MUST revoke when done) and the decoded duration in
   * seconds. Throws NarrationTtsError on any failure so callers can fall back.
   */
  async synthesize(text: string, voiceId?: string): Promise<NarrationTtsResult> {
    const trimmed = text?.trim();
    if (!trimmed) {
      throw new NarrationTtsError('synthesize() called with empty text.');
    }
    if (!this.isSupported()) {
      throw new NarrationTtsError('In-browser TTS unsupported (no WebAssembly / AudioContext).');
    }

    let blob: Blob;
    try {
      const predict = await this._getPredict();
      const effectiveVoice = voiceId || this.defaultVoiceId;

      blob = await predict!(
        { text: trimmed, voiceId: effectiveVoice },
        (p) => {
          if (p.total > 0) {
            const pct = Math.max(0, Math.min(100, Math.round((p.loaded / p.total) * 100)));
            this.modelProgress.set(pct);
          }
        }
      );
    } catch (err) {
      throw new NarrationTtsError('Piper TTS inference failed.', err);
    }

    if (!(blob instanceof Blob) || blob.size === 0) {
      throw new NarrationTtsError('Piper TTS returned an empty or invalid Blob.');
    }

    const objectUrl = URL.createObjectURL(blob);
    const durationSeconds = await this._decodeDuration(blob);

    return { blob, objectUrl, durationSeconds };
  }

  // ---------------------------------------------------------------------------

  /**
   * Lazy-load the self-hosted vits-web ESM and return its `predict` export.
   * The import specifier is a variable (`this._libUrl`) so:
   *   - esbuild does NOT analyze/bundle the file or its transitive imports.
   *   - TypeScript infers `Promise<any>` — no vits-web / ort type graph pulled
   *     in, so no @types/node Timeout leak into the app's type program.
   */
  private _getPredict(): Promise<PiperPredictFn | null> {
    if (this._predict) return Promise.resolve(this._predict);
    if (this._loadPromise) return this._loadPromise;

    this._loadPromise = (async () => {
      this.modelLoading.set(true);
      this.modelProgress.set(0);
      try {
        // IMPORTANT: `url` must be a variable, not a literal, so esbuild skips
        // static bundling. TS sees Promise<any> and skips type resolution.
        const url: string = this._libUrl;
        const mod: any = await import(url);
        if (typeof mod?.predict !== 'function') {
          throw new Error('vits-web-local.js did not export a `predict` function.');
        }
        this._predict = mod.predict as PiperPredictFn;
        this.modelReady.set(true);
        return this._predict;
      } catch (err) {
        this._loadPromise = null; // allow retry on next call
        throw new NarrationTtsError('Failed to load Piper TTS library.', err);
      } finally {
        this.modelLoading.set(false);
      }
    })();

    return this._loadPromise;
  }

  /**
   * Decode the WAV blob with a throwaway AudioContext to get exact duration.
   * Falls back to 0 if AudioContext is unavailable or decoding fails.
   */
  private async _decodeDuration(blob: Blob): Promise<number> {
    try {
      const AC = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext;
      if (!AC) return 0;
      const ctx = new AC();
      try {
        const arrayBuffer = await blob.arrayBuffer();
        const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
        return audioBuffer.duration;
      } finally {
        ctx.close().catch(() => {});
      }
    } catch {
      return 0;
    }
  }
}
